import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Grid,
  Chip,
  TablePagination,
  TextField,
  InputAdornment,
  CircularProgress,
  Backdrop,
  Button,
  Snackbar,
  Alert,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import {
  Hotel as HotelIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  TrendingUp as TrendingUpIcon,
  Receipt as ReceiptIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  ArrowBack as ArrowBackIcon,
  CalendarMonth as CalendarIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';

// Import API handler
import { getPostPaidBooking, getSpecificHotelBooking, setBookingType } from '../api/adminPanelApi';

// Format currency for display
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2
  }).format(amount);
};

const AdminPanelPostpaid = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [hotels, setHotels] = useState([]);
  const [totalData, setTotalData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Hotel detail states
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Booking selection and removal states
  const [selectedBookings, setSelectedBookings] = useState([]);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [removingBookings, setRemovingBookings] = useState(false);
  
  // Date filter states for main page
  const [mainSelectedMonth, setMainSelectedMonth] = useState('');
  const [mainStartDate, setMainStartDate] = useState('');
  const [mainEndDate, setMainEndDate] = useState('');

  // Initialize date filters for main page
  useEffect(() => {
    const now = new Date();
    const currentMonth = now.toISOString().slice(0, 7); // YYYY-MM format
    const currentDate = now.toISOString().slice(0, 10); // YYYY-MM-DD format
    const firstDayOfMonth = `${currentMonth}-01`;
    
    setMainSelectedMonth(currentMonth);
    setMainStartDate(firstDayOfMonth);
    setMainEndDate(currentDate);
  }, []);

  // Fetch postpaid booking data on component mount
  useEffect(() => {
    fetchPostpaidData();
  }, []);

  // Preselect hotel when navigated from Bookings panel (once)
  const stateHotelId = location.state?.selectedHotelId;
  const stateMonth = location.state?.month;
  const stateStart = location.state?.start;
  const stateEnd = location.state?.end;
  const appliedStateRef = React.useRef(false);
  useEffect(() => {
    if (appliedStateRef.current || !stateHotelId || hotels.length === 0) return;
    const match = hotels.find((h) => String(h.hotel_id) === String(stateHotelId));
    if (match) {
      setSelectedHotel(match);
      appliedStateRef.current = true;
    }
    if (stateMonth && stateStart && stateEnd) {
      setMainSelectedMonth(stateMonth);
      setMainStartDate(stateStart);
      setMainEndDate(stateEnd);
    }
  }, [stateHotelId, hotels, stateMonth, stateStart, stateEnd]);

  // Initialize date filters when a hotel is selected
  useEffect(() => {
    if (selectedHotel) {
      const now = new Date();
      const currentMonth = now.toISOString().slice(0, 7); // YYYY-MM format
      const currentDate = now.toISOString().slice(0, 10); // YYYY-MM-DD format
      const firstDayOfMonth = `${currentMonth}-01`;
      
      setSelectedMonth(currentMonth);
      setStartDate(firstDayOfMonth);
      setEndDate(currentDate);
      
      // Fetch bookings for the current month
      fetchHotelBookings(selectedHotel.hotel_id, currentMonth, firstDayOfMonth, currentDate);
    }
  }, [selectedHotel]);

  // Fetch postpaid booking data from API
  const fetchPostpaidData = async () => {
    setLoading(true);
    try {
      // Get current month date range
      const now = new Date();
      const currentMonth = now.toISOString().slice(0, 7); // YYYY-MM format
      const currentDate = now.toISOString().slice(0, 10); // YYYY-MM-DD format
      const firstDayOfMonth = `${currentMonth}-01`;
      
      const result = await getPostPaidBooking(currentMonth, firstDayOfMonth, currentDate);
      
      if (result.success) {
        setHotels(result.data.perHotelData);
        setTotalData(result.data.totalData);
        setSnackbar({
          open: true,
          message: `Loaded ${result.data.perHotelData.length} hotels successfully`,
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: result.error,
          severity: 'error'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to fetch postpaid booking data',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Utility functions for date management
  const getCurrentMonthDateRange = (monthStr) => {
    const now = new Date();
    const selectedDate = new Date(monthStr + '-01');
    const currentMonth = now.toISOString().slice(0, 7);
    
    if (monthStr === currentMonth) {
      // Current month: start from 1st, end at current date
      return {
        start: `${monthStr}-01`,
        end: now.toISOString().slice(0, 10)
      };
    } else if (monthStr < currentMonth) {
      // Past month: start from 1st, end at last day of that month
      const lastDay = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0).getDate();
      return {
        start: `${monthStr}-01`,
        end: `${monthStr}-${lastDay.toString().padStart(2, '0')}`
      };
    } else {
      // Future month: not allowed, return current month
      return getCurrentMonthDateRange(currentMonth);
    }
  };

  const fetchHotelBookings = async (hotelId, month, startDate, endDate) => {
    try {
      setBookingsLoading(true);
      const response = await getSpecificHotelBooking(hotelId, month, startDate, endDate, 'postPaid');
      
      if (response.success && response.data && response.data.bookingData) {
        setBookings(response.data.bookingData);
      } else {
        setBookings([]);
        setSnackbar({
          open: true,
          message: response.error || 'No bookings found for the selected period',
          severity: 'info'
        });
      }
    } catch (error) {
      console.error('Error fetching hotel bookings:', error);
      setBookings([]);
      setSnackbar({
        open: true,
        message: 'Failed to fetch hotel bookings',
        severity: 'error'
      });
    } finally {
      setBookingsLoading(false);
    }
  };

  // Event handlers
  const handleHotelClick = (hotel) => {
    setSelectedHotel(hotel);
  };

  const handleBackToHotels = () => {
    setSelectedHotel(null);
    setBookings([]);
    setSelectedBookings([]);
  };

  const handleMonthChange = (event) => {
    const newMonth = event.target.value;
    const now = new Date();
    const currentMonth = now.toISOString().slice(0, 7);
    
    // Prevent future month selection
    if (newMonth > currentMonth) {
      setSnackbar({
        open: true,
        message: 'Cannot select future months as no data is available',
        severity: 'warning'
      });
      return;
    }
    
    setSelectedMonth(newMonth);
    const dateRange = getCurrentMonthDateRange(newMonth);
    setStartDate(dateRange.start);
    setEndDate(dateRange.end);
    
    // Fetch bookings for the new month
    if (selectedHotel) {
      fetchHotelBookings(selectedHotel.hotel_id, newMonth, dateRange.start, dateRange.end);
    }
  };

  const handleDateChange = (field, value) => {
    if (field === 'start') {
      setStartDate(value);
    } else {
      setEndDate(value);
    }
  };

  const handleApplyDateFilter = () => {
    if (selectedHotel && selectedMonth && startDate && endDate) {
      fetchHotelBookings(selectedHotel.hotel_id, selectedMonth, startDate, endDate);
    }
  };

  // Booking selection handlers
  const handleBookingSelection = (bookingId, isChecked) => {
    if (isChecked) {
      setSelectedBookings(prev => [...prev, bookingId]);
    } else {
      setSelectedBookings(prev => prev.filter(id => id !== bookingId));
    }
  };

  const handleSelectAllBookings = (isChecked) => {
    if (isChecked) {
      const allBookingIds = bookings.map(booking => booking.booking_id);
      setSelectedBookings(allBookingIds);
    } else {
      setSelectedBookings([]);
    }
  };

  // Remove bookings handlers
  const handleRemoveClick = () => {
    if (selectedBookings.length === 0) {
      setSnackbar({
        open: true,
        message: 'Please select bookings to remove',
        severity: 'warning'
      });
      return;
    }
    setRemoveDialogOpen(true);
  };

  const handleRemoveConfirm = async () => {
    try {
      setRemovingBookings(true);
      const response = await setBookingType(selectedBookings, 'test');
      
      if (response.success) {
        setSnackbar({
          open: true,
          message: `Successfully removed ${selectedBookings.length} booking(s)`,
          severity: 'success'
        });
        
        // Refresh bookings
        if (selectedHotel) {
          fetchHotelBookings(selectedHotel.hotel_id, selectedMonth, startDate, endDate);
        }
        setSelectedBookings([]);
      } else {
        setSnackbar({
          open: true,
          message: response.error || 'Failed to remove bookings',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error removing bookings:', error);
      setSnackbar({
        open: true,
        message: 'An error occurred while removing bookings',
        severity: 'error'
      });
    } finally {
      setRemovingBookings(false);
      setRemoveDialogOpen(false);
    }
  };

  const handleRemoveCancel = () => {
    setRemoveDialogOpen(false);
  };

  // Filter hotels based on search term
  const filteredHotels = hotels.filter(hotel =>
    hotel.hotel_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    hotel.hotel_id.toString().includes(searchTerm) ||
    hotel.phone.includes(searchTerm)
  );

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Main page date filter handlers
  const handleMainMonthChange = (event) => {
    const newMonth = event.target.value;
    const now = new Date();
    const currentMonth = now.toISOString().slice(0, 7);
    
    // Prevent future month selection
    if (newMonth > currentMonth) {
      setSnackbar({
        open: true,
        message: 'Cannot select future months as no data is available',
        severity: 'warning'
      });
      return;
    }
    
    setMainSelectedMonth(newMonth);
    const dateRange = getCurrentMonthDateRange(newMonth);
    setMainStartDate(dateRange.start);
    setMainEndDate(dateRange.end);
  };

  const handleMainDateChange = (field, value) => {
    if (field === 'start') {
      setMainStartDate(value);
    } else {
      setMainEndDate(value);
    }
  };

  const handleMainApplyDateFilter = () => {
    // Refresh data with new date filters
    fetchPostpaidDataWithFilters();
  };

  const fetchPostpaidDataWithFilters = async () => {
    setLoading(true);
    try {
      const result = await getPostPaidBooking(mainSelectedMonth, mainStartDate, mainEndDate);
      
      if (result.success) {
        setHotels(result.data.perHotelData);
        setTotalData(result.data.totalData);
        setSnackbar({
          open: true,
          message: `Loaded ${result.data.perHotelData.length} hotels successfully`,
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: result.error,
          severity: 'error'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to fetch postpaid booking data',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Get paginated hotels
  const paginatedHotels = filteredHotels.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Hotel Detail View
  if (selectedHotel) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
          <IconButton onClick={handleBackToHotels} sx={{ color: '#FF7700' }}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" fontWeight={700} sx={{ color: '#2E4057' }}>
              {selectedHotel.hotel_name}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Hotel ID: {selectedHotel.hotel_id} | Phone: {selectedHotel.phone}
            </Typography>
          </Box>
        </Box>

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
              border: '1px solid #90caf9',
              boxShadow: '0 2px 8px rgba(33, 150, 243, 0.1)'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <BusinessIcon sx={{ fontSize: 24, color: '#1976d2' }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h4" fontWeight={700} sx={{ color: '#1565c0', fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}>
                      {selectedHotel.totalBookings}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#1976d2' }}>Total Bookings</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)',
              border: '1px solid #ce93d8',
              boxShadow: '0 2px 8px rgba(156, 39, 176, 0.1)'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <TrendingUpIcon sx={{ fontSize: 24, color: '#7b1fa2' }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h4" fontWeight={700} sx={{ color: '#6a1b9a', fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}>
                      {formatCurrency(selectedHotel.totalRevenue)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#7b1fa2' }}>Total Revenue</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)',
              border: '1px solid #a5d6a7',
              boxShadow: '0 2px 8px rgba(76, 175, 80, 0.1)'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <ReceiptIcon sx={{ fontSize: 24, color: '#388e3c' }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h4" fontWeight={700} sx={{ color: '#2e7d32', fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}>
                      {formatCurrency(selectedHotel.totalGst)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#388e3c' }}>GST</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
              border: '1px solid #ffcc80',
              boxShadow: '0 2px 8px rgba(255, 152, 0, 0.1)'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <HotelIcon sx={{ fontSize: 24, color: '#f57c00' }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h4" fontWeight={700} sx={{ color: '#ef6c00', fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}>
                      {formatCurrency(selectedHotel.tabtripsTotal)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#f57c00' }}>TabTrips Total</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Date Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" fontWeight={600} sx={{ color: '#2E4057', mb: 3 }}>
            Booking Filters
          </Typography>
          
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Month</InputLabel>
                <Select
                  value={selectedMonth}
                  label="Month"
                  onChange={handleMonthChange}
                  startAdornment={<CalendarIcon sx={{ mr: 1, color: '#FF7700' }} />}
                >
                  {Array.from({ length: 12 }, (_, i) => {
                    const date = new Date();
                    date.setMonth(date.getMonth() - i);
                    const monthValue = date.toISOString().slice(0, 7);
                    const monthLabel = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
                    return (
                      <MenuItem key={monthValue} value={monthValue}>
                        {monthLabel}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={startDate}
                onChange={(e) => handleDateChange('start', e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  max: new Date().toISOString().slice(0, 10)
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={endDate}
                onChange={(e) => handleDateChange('end', e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  max: new Date().toISOString().slice(0, 10)
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleApplyDateFilter}
                sx={{ 
                  backgroundColor: '#FF7700',
                  '&:hover': { backgroundColor: '#e56900' },
                  py: 1.5
                }}
                disabled={bookingsLoading}
              >
                Apply Filter
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Bookings Table */}
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <Box sx={{ p: 3, borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight={600} sx={{ color: '#2E4057' }}>
              Postpaid Bookings ({bookings.length})
            </Typography>
            
            {selectedBookings.length > 0 && (
              <Button
                variant="contained"
                startIcon={<DeleteIcon />}
                onClick={handleRemoveClick}
                disabled={removingBookings}
                sx={{
                  backgroundColor: '#f44336',
                  '&:hover': { backgroundColor: '#d32f2f' }
                }}
              >
                {removingBookings ? 'Removing...' : `Remove (${selectedBookings.length})`}
              </Button>
            )}
          </Box>
          
          {bookingsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress sx={{ color: '#FF7700' }} />
            </Box>
          ) : bookings.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                No bookings found for the selected period
              </Typography>
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>
                      <Checkbox
                        checked={selectedBookings.length === bookings.length && bookings.length > 0}
                        indeterminate={selectedBookings.length > 0 && selectedBookings.length < bookings.length}
                        onChange={(e) => handleSelectAllBookings(e.target.checked)}
                        disabled={bookings.length === 0}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>Booking ID</TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>Guest Name</TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>Guest Phone</TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>Check-in</TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>Check-out</TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }} align="right">Total Amount</TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }} align="right">GST</TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }} align="center">Booking Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bookings.map((booking) => {
                    const isSelected = selectedBookings.includes(booking.booking_id);
                    
                    return (
                      <TableRow key={booking.booking_id} hover>
                        <TableCell>
                          <Checkbox
                            checked={isSelected}
                            onChange={(e) => handleBookingSelection(booking.booking_id, e.target.checked)}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={booking.booking_id} 
                            size="small" 
                            sx={{ backgroundColor: '#FF7700', color: 'white' }}
                          />
                        </TableCell>
                        <TableCell>{booking.guest_name}</TableCell>
                        <TableCell>{booking.guest_phone}</TableCell>
                        <TableCell>{new Date(booking.check_in_date).toLocaleDateString()}</TableCell>
                        <TableCell>{new Date(booking.check_out_date).toLocaleDateString()}</TableCell>
                        <TableCell align="right">{formatCurrency(parseFloat(booking.discounted_amount))}</TableCell>
                        <TableCell align="right">{formatCurrency(parseFloat(booking.gst_amount))}</TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={booking.booking_status} 
                            size="small" 
                            sx={{ 
                              backgroundColor: booking.booking_status === 'Confirmed' ? '#4caf50' : 
                                             booking.booking_status === 'No Show' ? '#f44336' : '#ff9800',
                              color: 'white'
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
        
        {/* Remove Confirmation Dialog */}
        <Dialog
          open={removeDialogOpen}
          onClose={handleRemoveCancel}
          aria-labelledby="remove-dialog-title"
          aria-describedby="remove-dialog-description"
        >
          <DialogTitle id="remove-dialog-title" sx={{ color: '#d32f2f' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WarningIcon sx={{ color: '#d32f2f' }} />
              Confirm Booking Removal
            </Box>
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="remove-dialog-description">
              Are you sure you want to remove {selectedBookings.length} selected booking(s)? 
              This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={handleRemoveCancel} 
              color="primary"
              disabled={removingBookings}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleRemoveConfirm} 
              color="error" 
              variant="contained"
              disabled={removingBookings}
              startIcon={removingBookings ? <CircularProgress size={16} /> : <DeleteIcon />}
            >
              {removingBookings ? 'Removing...' : 'Yes, Remove'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert 
            onClose={() => setSnackbar({ ...snackbar, open: false })} 
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Loading Backdrop */}
      <Backdrop open={loading} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton 
              onClick={() => navigate('/')} 
              sx={{ 
                color: '#FF7700',
                '&:hover': { 
                  backgroundColor: 'rgba(255, 119, 0, 0.1)',
                  transform: 'scale(1.05)'
                },
                transition: 'all 0.2s ease-in-out'
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" fontWeight={700} sx={{ color: '#2E4057' }}>
              Admin Panel - Postpaid Bookings
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchPostpaidData}
            disabled={loading}
            sx={{ 
              borderColor: '#FF7700',
              color: '#FF7700',
              '&:hover': {
                borderColor: '#e65100',
                backgroundColor: 'rgba(255, 119, 0, 0.04)'
              }
            }}
          >
            Refresh Data
          </Button>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Overview of all postpaid hotel bookings with no-show tracking
        </Typography>
      </Box>

      {/* Statistics Cards */}
      {totalData && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={2}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
              border: '1px solid #90caf9',
              boxShadow: '0 2px 8px rgba(33, 150, 243, 0.1)'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <BusinessIcon sx={{ fontSize: 24, color: '#1976d2' }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h4" fontWeight={700} sx={{ color: '#1565c0', fontSize: { xs: '1.2rem', sm: '1.5rem', md: '1.75rem' } }}>
                      {totalData.totalBookings}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#1976d2' }}>Total Bookings</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)',
              border: '1px solid #a5d6a7',
              boxShadow: '0 2px 8px rgba(76, 175, 80, 0.1)'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <TrendingUpIcon sx={{ fontSize: 24, color: '#388e3c' }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h4" fontWeight={700} sx={{ color: '#2e7d32', fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.3rem' } }}>
                      {formatCurrency(totalData.totalRevenue)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#388e3c' }}>Total Revenue</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
              border: '1px solid #ffcc80',
              boxShadow: '0 2px 8px rgba(255, 152, 0, 0.1)'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <ReceiptIcon sx={{ fontSize: 24, color: '#f57c00' }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h4" fontWeight={700} sx={{ color: '#ef6c00', fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.3rem' } }}>
                      {formatCurrency(totalData.totalGst)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#f57c00' }}>Total GST</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #fce4ec 0%, #f8bbd9 100%)',
              border: '1px solid #f48fb1',
              boxShadow: '0 2px 8px rgba(233, 30, 99, 0.1)'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <ReceiptIcon sx={{ fontSize: 24, color: '#c2185b' }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h4" fontWeight={700} sx={{ color: '#ad1457', fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.3rem' } }}>
                      {formatCurrency(totalData.totalTTCommission)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#c2185b' }}>Commission</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)',
              border: '1px solid #ce93d8',
              boxShadow: '0 2px 8px rgba(156, 39, 176, 0.1)'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <HotelIcon sx={{ fontSize: 24, color: '#7b1fa2' }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h4" fontWeight={700} sx={{ color: '#6a1b9a', fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.3rem' } }}>
                      {formatCurrency(totalData.tabtripsTotal)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#7b1fa2' }}>TabTrips Total</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)',
              border: '1px solid #ef9a9a',
              boxShadow: '0 2px 8px rgba(244, 67, 54, 0.1)'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <WarningIcon sx={{ fontSize: 24, color: '#d32f2f' }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h4" fontWeight={700} sx={{ color: '#c62828', fontSize: { xs: '1.2rem', sm: '1.5rem', md: '1.75rem' } }}>
                      {totalData.totalNoShow || 0}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#d32f2f' }}>No Show Bookings</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Search Bar */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search hotels by name, ID, or phone number..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            )
          }}
        />
      </Paper>

      {/* Date Filters */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" fontWeight={600} sx={{ color: '#2E4057', mb: 3 }}>
          Date Filters
        </Typography>
        
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Month</InputLabel>
              <Select
                value={mainSelectedMonth}
                label="Month"
                onChange={handleMainMonthChange}
                startAdornment={<CalendarIcon sx={{ mr: 1, color: '#FF7700' }} />}
              >
                {Array.from({ length: 12 }, (_, i) => {
                  const date = new Date();
                  date.setMonth(date.getMonth() - i);
                  const monthValue = date.toISOString().slice(0, 7);
                  const monthLabel = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
                  return (
                    <MenuItem key={monthValue} value={monthValue}>
                      {monthLabel}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={mainStartDate}
              onChange={(e) => handleMainDateChange('start', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                max: new Date().toISOString().slice(0, 10)
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="End Date"
              type="date"
              value={mainEndDate}
              onChange={(e) => handleMainDateChange('end', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                max: new Date().toISOString().slice(0, 10)
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleMainApplyDateFilter}
              sx={{ 
                backgroundColor: '#FF7700',
                '&:hover': { backgroundColor: '#e56900' },
                py: 1.5
              }}
              disabled={loading}
            >
              Apply Filter
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Hotels Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <Box sx={{ p: 3, borderBottom: '1px solid #e0e0e0' }}>
          <Typography variant="h6" fontWeight={600} sx={{ color: '#2E4057' }}>
            Hotels Revenue Overview ({filteredHotels.length} hotels)
          </Typography>
        </Box>

        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>Hotel ID</TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>Hotel Name</TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>Contact</TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>Total Bookings</TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>No Show</TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>Total Revenue</TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>GST</TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>Commission</TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>TabTrips Total</TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedHotels.map((hotel) => (
                <TableRow 
                  key={hotel.hotel_id} 
                  hover 
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleHotelClick(hotel)}
                >
                  <TableCell>
                    <Chip 
                      label={hotel.hotel_id} 
                      size="small" 
                      sx={{ backgroundColor: '#FF7700', color: 'white' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <HotelIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2" fontWeight={500}>
                        {hotel.hotel_name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2">{hotel.phone}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={hotel.totalBookings} 
                      size="small" 
                      sx={{ backgroundColor: '#43e97b', color: 'white' }}
                    />
                  </TableCell>
                  <TableCell>
                    {hotel.totalNoShow > 0 ? (
                      <Chip 
                        label={hotel.totalNoShow} 
                        size="small" 
                        sx={{ backgroundColor: '#f44336', color: 'white' }}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">0</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500} sx={{ color: '#2e7d32' }}>
                      {formatCurrency(hotel.totalRevenue)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: '#f57c00' }}>
                      {formatCurrency(hotel.totalGst)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: '#c2185b' }}>
                      {formatCurrency(hotel.totalTTCommission)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600} sx={{ color: '#7b1fa2' }}>
                      {formatCurrency(hotel.tabtripsTotal)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton 
                      size="small" 
                      sx={{ color: '#FF7700' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleHotelClick(hotel);
                      }}
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredHotels.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />

        {filteredHotels.length === 0 && !loading && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              {searchTerm ? 'No hotels found matching your search criteria.' : 'No postpaid booking data available.'}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminPanelPostpaid; 